import { useMemo } from 'react';
import { useGetList } from 'react-admin';
import { FAILED_PAYMENT_STATUSES } from '../Subscriptions/subscriptionShared';
import { BillingAccountRow, OrphanUsageRow } from './billingAlertsState';
import { PeriodRange } from './PeriodFilter';

/**
 * Les totaux portent sur TOUS les comptes, pas sur la page affichée : sommer les
 * 25 lignes d'une page et présenter le résultat comme un total donnerait un
 * chiffre faux dès qu'il y a plus d'une page. On paie donc une requête dédiée,
 * qui ramène quelques dizaines de lignes.
 */
const SUMMARY_PAGE_SIZE = 500;

export interface BillingAlertsSummary {
	/** État courant, indépendant de la plage. */
	failedPaymentCount: number;
	unpaidDwAccounts: number;
	managedProjects: number;

	/** Mesuré sur la plage, en euros : la dépense réelle de Quanti. */
	measuredEur: number;
	storageEur: number;
	unpaidDwEur: number;
	orphanEur: number;

	/** Vrai au-delà de SUMMARY_PAGE_SIZE comptes : les totaux sont alors partiels. */
	truncated: boolean;
	/** À consommer : un zéro affiché pendant le chargement se lit comme un fait. */
	isLoading: boolean;
}

const sumBy = <T,>(rows: T[], pick: (row: T) => number): number =>
	rows.reduce((total, row) => total + (pick(row) || 0), 0);

export const useBillingAlertsSummary = (range: PeriodRange): BillingAlertsSummary => {
	const accounts = useGetList<BillingAccountRow>('billing-account-dw-usage', {
		pagination: { page: 1, perPage: SUMMARY_PAGE_SIZE },
		sort: { field: 'totalEur', order: 'DESC' },
		filter: { from: range.from, to: range.to },
	});

	const orphans = useGetList<OrphanUsageRow>('orphan-dw-usage', {
		pagination: { page: 1, perPage: SUMMARY_PAGE_SIZE },
		sort: { field: 'totalEur', order: 'DESC' },
		filter: { from: range.from, to: range.to },
	});

	// Le compteur d'impayés se lit sur les subscriptions, pas sur les comptes
	// ci-dessus : un compte peut être en past_due sans porter aucun projet, il
	// n'apparaît alors dans aucune ligne de conso mais doit être compté.
	const failedPayments = useGetList('subscriptions', {
		pagination: { page: 1, perPage: 1 },
		sort: { field: 'current_period_end', order: 'ASC' },
		filter: { status: FAILED_PAYMENT_STATUSES },
	});

	const totals = useMemo(() => {
		const rows = accounts.data || [];
		const orphanRows = orphans.data || [];
		// `alert` vient du back : c'est le même booléen qui trie le tableau et
		// teinte les montants, donc ce compteur ne peut pas le contredire.
		const unpaid = rows.filter((row) => row.alert);

		return {
			unpaidDwAccounts: unpaid.length,
			managedProjects: sumBy(rows, (row) => row.managedProjectCount),

			measuredEur: sumBy(rows, (row) => row.totalEur) + sumBy(orphanRows, (row) => row.totalEur),
			storageEur:
				sumBy(rows, (row) => row.storageEur) + sumBy(orphanRows, (row) => row.storageEur),
			unpaidDwEur: sumBy(unpaid, (row) => row.totalEur),
			orphanEur: sumBy(orphanRows, (row) => row.totalEur),
		};
	}, [accounts.data, orphans.data]);

	return {
		...totals,
		failedPaymentCount: failedPayments.total ?? 0,
		truncated: (accounts.total ?? 0) > (accounts.data?.length ?? 0),
		isLoading: accounts.isLoading || orphans.isLoading || failedPayments.isLoading,
	};
};
